import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getSigningSessions } from 'app/entities/signing-session/signing-session.reducer';
import { SignatureStatus } from 'app/shared/model/enumerations/signature-status.model';
import { createEntity, getEntity, reset, updateEntity } from './contract-signature.reducer';

export const ContractSignatureUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const signingSessions = useAppSelector(state => state.signingSession.entities);
  const contractSignatureEntity = useAppSelector(state => state.contractSignature.entity);
  const loading = useAppSelector(state => state.contractSignature.loading);
  const updating = useAppSelector(state => state.contractSignature.updating);
  const updateSuccess = useAppSelector(state => state.contractSignature.updateSuccess);
  const signatureStatusValues = Object.keys(SignatureStatus);

  const handleClose = () => {
    navigate('/contract-signature');
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getSigningSessions({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    values.signedTimestamp = convertDateTimeToServer(values.signedTimestamp);
    values.createdTimestamp = convertDateTimeToServer(values.createdTimestamp);

    const entity = {
      ...contractSignatureEntity,
      ...values,
      signingSession: signingSessions.find(it => it.id.toString() === values.signingSession?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          signedTimestamp: displayDefaultDateTime(),
          createdTimestamp: displayDefaultDateTime(),
        }
      : {
          status: 'CREATED',
          ...contractSignatureEntity,
          signedTimestamp: convertDateTimeFromServer(contractSignatureEntity.signedTimestamp),
          createdTimestamp: convertDateTimeFromServer(contractSignatureEntity.createdTimestamp),
          signingSession: contractSignatureEntity?.signingSession?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2
            id="crawlerBackendApp.contractSignature.home.createOrEditLabel"
            data-cy="ContractSignatureCreateUpdateHeading"
          >
            <Translate contentKey="crawlerBackendApp.contractSignature.home.createOrEditLabel">
              Create or edit a ContractSignature
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="contract-signature-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('crawlerBackendApp.contractSignature.status')}
                id="contract-signature-status"
                name="status"
                data-cy="status"
                type="select"
              >
                {signatureStatusValues.map(signatureStatus => (
                  <option value={signatureStatus} key={signatureStatus}>
                    {translate(`crawlerBackendApp.SignatureStatus.${signatureStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('crawlerBackendApp.contractSignature.signerName')}
                id="contract-signature-signerName"
                name="signerName"
                data-cy="signerName"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.contractSignature.signerEmail')}
                id="contract-signature-signerEmail"
                name="signerEmail"
                data-cy="signerEmail"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.contractSignature.ipAddress')}
                id="contract-signature-ipAddress"
                name="ipAddress"
                data-cy="ipAddress"
                type="text"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.contractSignature.signatureValue')}
                id="contract-signature-signatureValue"
                name="signatureValue"
                data-cy="signatureValue"
                type="textarea"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.contractSignature.signedTimestamp')}
                id="contract-signature-signedTimestamp"
                name="signedTimestamp"
                data-cy="signedTimestamp"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('crawlerBackendApp.contractSignature.createdTimestamp')}
                id="contract-signature-createdTimestamp"
                name="createdTimestamp"
                data-cy="createdTimestamp"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('crawlerBackendApp.contractSignature.rejectionReason')}
                id="contract-signature-rejectionReason"
                name="rejectionReason"
                data-cy="rejectionReason"
                type="text"
              />
              <ValidatedField
                id="contract-signature-signingSession"
                name="signingSession"
                data-cy="signingSession"
                label={translate('crawlerBackendApp.contractSignature.signingSession')}
                type="select"
              >
                <option value="" key="0" />
                {signingSessions
                  ? signingSessions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/contract-signature"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ContractSignatureUpdate;
